import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ContractBillingUIService, Option } from '../contract-billing-ui.service';
import { Observable } from 'rxjs';

@Component({
  selector: 'contract-billing-create-customer-form',
  templateUrl: './create-customer-form.component.html',
  styleUrls: ['./create-customer-form.component.scss']
})
export class CreateCustomerFormComponent implements OnInit {
  form: FormGroup;

  bankNames$: Observable<string[]> = this.service.bankNames$;
  bankAccountTypes$: Observable<string[]> = this.service.bankAccountTypes$;
  paySources$: Observable<Option[]> = this.service.paySources$;
  payMethods$: Observable<Option[]> = this.service.payMethods$;
  bookCodes$: Observable<Option[]> = this.service.bookCodes$;
  editions$: Observable<Option[]> = this.service.editions$;

  constructor(
      private readonly formBuilder: FormBuilder,
      private readonly service: ContractBillingUIService
  ) { }

  ngOnInit() {

    this.form = this.formBuilder.group({
      name: ['', {validators: [Validators.required, Validators.maxLength(100)]}],
      website: ['', {validators: [Validators.maxLength(100)]}],
      cbNumber: ['', {validators: [Validators.maxLength(10)]}],
      cbRating: ['', {validators: [Validators.maxLength(5)]}],
      cbDate: [null],
      companyRegistrationNumber: ['', {validators: [Validators.maxLength(20)]}],
      creditVettingCode: ['', {validators: [Validators.maxLength(20)]}],
      vatRegistrationNumber: ['', {validators: [Validators.maxLength(20)]}],
      emailAddress: ['', {validators: [Validators.required, Validators.email, Validators.maxLength(500)]}],
      banks: this.formBuilder.array([], {validators: [Validators.required]}),
      billToAddresses: this.formBuilder.array([], {validators: [Validators.required]}),
      contactPersons: this.formBuilder.array([], {validators: [Validators.required]}),
      contracts: this.formBuilder.array([], {validators: [Validators.required]}),
    });
    this.addBank();
    this.addBillToAddress();
    this.addContactPerson();
    this.addContract();
  }

  getBanks(): FormArray {
    return (this.form.controls['banks'] as FormArray);
  }

  addBank(): void {
    this.getBanks().push(this.formBuilder.group({
      name: ['', {validators: [Validators.required]}],
      ibtCode: ['', {validators: [Validators.required, Validators.maxLength(8)]}],
      accountType: ['', {validators: [Validators.required, Validators.maxLength(60)]}],
      accountHolderName: ['', {validators: [Validators.required, Validators.maxLength(60)]}],
      accountNumber: ['', {validators: [Validators.required, Validators.maxLength(20)]}],
      mandateAuthorisedName: ['', {validators: [Validators.required, Validators.maxLength(50)]}],
      mandateReceived: [null],
      mandateDayFirst: ['', {validators: [Validators.required, Validators.max(31), Validators.min(1)]}],
      mandateDayRest: ['', {validators: [Validators.required, Validators.max(31), Validators.min(1)]}],
      isDefault: [false],
    }))
  }

  removeBank(index: number): void {
    this.getBanks().removeAt(index);
  }

  getBillToAddresses(): FormArray {
    return (this.form.controls['billToAddresses'] as FormArray);
  }

  addBillToAddress(): void {
    const control = this.formBuilder.group({
      addressLine1: ['', {validators: [Validators.required, Validators.maxLength(60)]}],
      addressLine2: ['', {validators: [Validators.required, Validators.maxLength(60)]}],
      addressLine3: ['', {validators: [Validators.required, Validators.maxLength(60)]}],
      addressLine4: ['', {validators: [Validators.maxLength(60)]}],
      postalCode: ['', {validators: [Validators.required, Validators.maxLength(10)]}],
      city: ['', {validators: [Validators.required, Validators.maxLength(30)]}],
      province: ['', {validators: [Validators.required, Validators.maxLength(30)]}],
      isDefault: [false],
    });
    this.getBillToAddresses().push(control);
  }

  removeBillToAddress(index: number): void {
    this.getBillToAddresses().removeAt(index);
  }

  getContactPersons(): FormArray {
    return (this.form.controls['contactPersons'] as FormArray);
  }

  addContactPerson(): void {
    this.getContactPersons().push(this.formBuilder.group({
      firstName: ['', {validators: [Validators.required, Validators.maxLength(60)]}],
      surname: ['', {validators: [Validators.required, Validators.maxLength(60)]}],
      title: ['', {validators: [Validators.required, Validators.maxLength(5)]}],
      contactPosition: ['', {validators: [Validators.required, Validators.maxLength(30)]}],
      phoneNumber: ['', {validators: [Validators.required, Validators.maxLength(30)]}],
      faxNumber: ['', {validators: [Validators.maxLength(30)]}],
      mobileNumber: ['', {validators: [Validators.maxLength(30)]}],
      emailAddress: ['', {validators: [Validators.required, Validators.email, Validators.maxLength(200)]}],
      isContractSigner: [false],
      isDefault: [false],
    }));
    this.getContactPersons().at(0).reset();
  }

  removeContactPerson(index: number): void {
    this.getContactPersons().removeAt(index);
  }

  getContracts(): FormArray {
    return (this.form.controls['contracts'] as FormArray);
  }

  addContract(): void {
    this.getContracts().push(this.formBuilder.group({
      bookCode: ['', {validators: [Validators.required]}],
      edition: ['', {validators: [Validators.required]}],
      contractTypeId: ['', {validators: [Validators.required]}],
      programId: ['', {validators: [Validators.required]}],
      referenceNumber: ['', {validators: [Validators.required, Validators.maxLength(30)]}],
      canvas: ['', {validators: [Validators.required, Validators.maxLength(2)]}],
      canvasYear: ['', {validators: [Validators.required, Validators.maxLength(2)]}],
      paySourceId: ['', {validators: [Validators.required]}],
      payMethodId: ['', {validators: [Validators.required]}],
      billingPhoneNumber: ['', {validators: [Validators.required, Validators.maxLength(20)]}],
      totalNumberOfBillingPeriods: ['', {validators: [Validators.required, Validators.min(1)]}],
      totalContractAmountExclTax: ['', {validators: [Validators.required]}],
      totalVolumeDiscountAmountExclTax: ['', {validators: [Validators.required]}],
      repCode: ['', {validators: [Validators.required, Validators.maxLength(10)]}],
    }))
  }

  removeContract(index: number): void {
    this.getContracts().removeAt(index);
  }

  onSubmit(): void {
    if (this.form.valid) {
      alert('valid');
    } else {
      console.log(this.form);
    }
  }
}
